import { Badge, Button, Callout, Card, CardHeader, Table, Tbody, Td, Th, Thead, Tooltip, Tr } from '@provenance/design';
import { describeFields, type ExtractionPanel as Panel } from '@provenance/shared';
import { AlertTriangle, Bot, Sparkles } from 'lucide-react';

/**
 * What the AI read off the certificate — spec v4 §24.
 *
 * On upload and email there is no submitted payload, so this is the only structured view of
 * the document. Two things are therefore non-negotiable in how it presents:
 *
 * 1. **It says what it is.** Every value here was read by a machine, not declared by the
 *    exporter. The heading says so, and the provenance badge says so, because a reader who
 *    assumes otherwise could pass a transcription error to a customs authority.
 * 2. **Low confidence is shown, not hidden.** A field the model was unsure about is more
 *    useful flagged than quietly rendered as if it were certain.
 */
export function ExtractionPanel({
  panel,
  onUseSuggestedType,
}: {
  panel: Panel;
  onUseSuggestedType?: (documentTypeId: string) => void;
}) {
  if (panel.provenance === 'ASSERTED') return null;

  if (panel.state !== 'COMPLETE' || !panel.data) {
    return (
      <Card>
        <CardHeader
          title="Details read from the certificate"
          description="This request arrived without submitted data."
        />
        <div className="p-4">
          <Callout
            tone="warn"
            icon={<AlertTriangle className="size-4" />}
            title="Nothing could be read automatically"
          >
            {panel.unavailableReason ??
              'The details could not be read from this certificate. Check every field against the document itself.'}
          </Callout>
        </div>
      </Card>
    );
  }

  const d = panel.data;
  const conf = (field: string) => d.confidence?.[field];

  return (
    <Card>
      <CardHeader
        title="Details read from the certificate"
        description="Read by software from the PDF — not submitted by the exporter. Check anything that matters before approving."
        actions={
          <Tooltip content={panel.model ? `Extracted by ${panel.model}` : undefined}>
            <span>
              <Badge tone="info" size="sm">
                <Bot aria-hidden className="size-2.5" />
                Extracted
              </Badge>
            </span>
          </Tooltip>
        }
      />

      <div className="flex flex-col gap-4 p-4">
        {/* The suggestion that matters most: the type drives the stamping layout and the
            number format, so a wrong one produces a correctly-stamped certificate of the
            wrong kind. Never applied automatically. */}
        {panel.suggestedDocumentTypeId && panel.suggestionDiffers && (
          <Callout
            tone="warn"
            icon={<Sparkles className="size-4" />}
            title={`This looks like a ${panel.suggestedDocumentTypeName}`}
          >
            <p>
              {d.documentTypeReasoning ??
                'The heading on the certificate suggests a different document type from the one this request is filed under.'}
              {typeof d.documentTypeConfidence === 'number' && (
                <> Confidence {Math.round(d.documentTypeConfidence * 100)}%.</>
              )}
            </p>
            <p className="mt-1">
              The type decides the stamping layout and the certificate number format, so it
              is worth being sure.
            </p>
            {onUseSuggestedType && (
              <div className="mt-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onUseSuggestedType(panel.suggestedDocumentTypeId!)}
                >
                  Change to {panel.suggestedDocumentTypeName}
                </Button>
              </div>
            )}
          </Callout>
        )}

        {d.unreadable.length > 0 && (
          <Callout tone="info" icon={<AlertTriangle className="size-4" />} title="Not legible">
            Left empty: {describeFields(d.unreadable)}.
          </Callout>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Party title="Exporter" party={d.exporter} confidence={conf('exporterCompanyName')} />
          <Party title="Consignee" party={d.consignee} confidence={conf('consigneeCompanyName')} />
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
          <Fact label="Invoice" value={d.invoiceNumber} confidence={conf('invoiceNumber')} />
          <Fact label="Invoice date" value={au(d.invoiceDate)} confidence={conf('invoiceDate')} />
          <Fact
            label="Invoice total"
            value={d.invoiceTotal ? `${d.invoiceTotal} ${d.currency ?? ''}`.trim() : null}
            confidence={conf('invoiceTotal')}
          />
          <Fact label="Destination" value={d.destinationCountry} confidence={conf('destinationCountry')} />
          <Fact label="Port of loading" value={d.portOfLoading} />
          <Fact label="Port of discharge" value={d.portOfDischarge} />
          <Fact label="Departure" value={au(d.departureDate)} />
          <Fact label="Vessel / flight" value={d.vesselOrFlight} />
        </dl>

        {d.goodsLines.length > 0 && (
          <div>
            <p className="mb-1.5 [font-size:12.5px] font-medium">
              {d.goodsLines.length} goods line{d.goodsLines.length === 1 ? '' : 's'}
            </p>
            <Table className="min-w-[640px]">
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Description</Th>
                  <Th>HS code</Th>
                  <Th numeric>Qty</Th>
                  <Th numeric>Weight</Th>
                  <Th numeric>Value</Th>
                  <Th>Origin</Th>
                </Tr>
              </Thead>
              <Tbody>
                {d.goodsLines.map((l, i) => (
                  <Tr key={i}>
                    <Td>{l.lineNumber ?? i + 1}</Td>
                    <Td>{l.description ?? <Missing />}</Td>
                    <Td>
                      <span className="pv-mono [font-size:12px]">{l.hsCode ?? '—'}</span>
                    </Td>
                    <Td numeric>{[l.quantity, l.unit].filter(Boolean).join(' ') || '—'}</Td>
                    <Td numeric>{[l.grossWeight, l.weightUnit].filter(Boolean).join(' ') || '—'}</Td>
                    <Td numeric>{l.value ?? '—'}</Td>
                    <Td>
                      {l.countryOfOrigin ?? '—'}
                      {l.originCriterion && (
                        <span className="ml-1 text-[var(--pv-faint)]">({l.originCriterion})</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}

        {/* Ties to the specimen signatures on file — on an uploaded or emailed declaration
            this is the strongest thing the chamber can check (§24.2). */}
        {d.declarationSignatoryName && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
            <Fact label="Signed by" value={d.declarationSignatoryName} confidence={conf('declarationSignatoryName')} />
            <Fact label="Position" value={d.declarationSignatoryPosition} />
            <Fact label="Signed on" value={au(d.declarationDate)} />
            <Fact label="At" value={d.declarationPlace} />
          </dl>
        )}
      </div>
    </Card>
  );
}

function Party({
  title,
  party,
  confidence,
}: {
  title: string;
  party: { companyName: string | null; abn: string | null; addressLine1: string | null; suburb: string | null; state: string | null; postcode: string | null; country: string | null };
  confidence?: number;
}) {
  return (
    <div className="rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3">
      <p className="[font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">
        {title}
      </p>
      <p className="mt-1 [font-size:13.5px] font-medium">
        {party.companyName ?? <Missing />} <Confidence value={confidence} />
      </p>
      {party.abn && <p className="pv-mono [font-size:12px] text-[var(--pv-muted)]">ABN {party.abn}</p>}
      <p className="mt-0.5 [font-size:12.5px] text-[var(--pv-muted)]">
        {[party.addressLine1, party.suburb, party.state, party.postcode, party.country]
          .filter(Boolean)
          .join(', ') || '—'}
      </p>
    </div>
  );
}

function Fact({ label, value, confidence }: { label: string; value: string | null; confidence?: number }) {
  return (
    <div>
      <dt className="[font-size:var(--pv-text-xs)]  text-[var(--pv-faint)]">
        {label}
      </dt>
      <dd className="mt-0.5 [font-size:13px]">
        {value ?? <Missing />} <Confidence value={confidence} />
      </dd>
    </div>
  );
}

/**
 * Shown only when the model was unsure.
 *
 * A confidence badge on every field would be noise that trains an operator to ignore all
 * of them — which would defeat the one case where it matters.
 */
function Confidence({ value }: { value?: number }) {
  if (typeof value !== 'number' || value >= 0.75) return null;
  return (
    <Tooltip content={`The software was ${Math.round(value * 100)}% confident reading this. Check it.`}>
      <span>
        <Badge tone="warn" size="sm">
          {Math.round(value * 100)}%
        </Badge>
      </span>
    </Tooltip>
  );
}

function Missing() {
  return <span className="text-[var(--pv-faint)]">not read</span>;
}

/** DD/MM/YYYY, per §8. */
function au(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-AU');
}
